// src/Reeg.jsx
import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Upload, Plus, Trash2, ArrowRight, ArrowLeft, Users, FileText } from "lucide-react";

import { toast } from '../../utils/toast.js';
const emptyMember = () => ({ fullName: "", role: "", email: "" });

const convertToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => resolve(reader.result);
  reader.onerror = (error) => reject(error);
});

// Persisted on every change so a reload or a back/forward navigation while still on this
// step doesn't lose what's been typed/uploaded — see the matching load/save effects below.
const DRAFT_KEY = "orgOnboardingDraftStepThree";
const defaultFormData = {
  secureSealText: "",
  secureSealAcknowledged: false,
  authorizedMembers: [emptyMember()]
};
const defaultDocuments = { logo: "", orgPresentation: "", orgPresentationName: "", secureSealImage: "" };

function Reeg() {
  const { t } = useTranslation();
  const [stepOneSnapshot, setStepOneSnapshot] = useState(null);
  const [stepTwoSnapshot, setStepTwoSnapshot] = useState(null);
  const [draft] = useState(() => {
    try {
      const saved = sessionStorage.getItem(DRAFT_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [formData, setFormData] = useState(() => draft?.formData ? { ...defaultFormData, ...draft.formData } : defaultFormData);
  const [documents, setDocuments] = useState(() => draft?.documents ? { ...defaultDocuments, ...draft.documents } : defaultDocuments);
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth <= 768 : false);

  const logoInputRef = useRef(null);
  const posterInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ formData, documents }));
    } catch {
      // Quota exceeded (large base64 uploads) — the typed fields still autosave next
      // render once documents shrink; uploads just won't survive a reload in that case.
    }
  }, [formData, documents]);

  useEffect(() => {
    const s1 = sessionStorage.getItem("orgOnboardingStepOne");
    const s2 = sessionStorage.getItem("orgOnboardingStepTwo");
    if (s1) setStepOneSnapshot(JSON.parse(s1)); else navigate("/reg");
    if (s2) setStepTwoSnapshot(JSON.parse(s2));
  }, [navigate]);

  const handleFileUpload = async (file, key) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast(t("authPages.orgReg.alertFileSize")); return; }
    const base64 = await convertToBase64(file);
    setDocuments(prev => ({ ...prev, [key]: base64, ...(key === "orgPresentation" ? { orgPresentationName: file.name } : {}) }));
  };

  const updateMember = (index, field, value) => setFormData(prev => {
    const next = [...prev.authorizedMembers];
    next[index] = { ...next[index], [field]: value };
    return { ...prev, authorizedMembers: next };
  });
  const addMember = () => { if (formData.authorizedMembers.length >= 10) return; setFormData(prev => ({ ...prev, authorizedMembers: [...prev.authorizedMembers, emptyMember()] })); };
  const removeMember = (index) => setFormData(prev => ({ ...prev, authorizedMembers: prev.authorizedMembers.filter((_, i) => i !== index) }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const lead = formData.authorizedMembers[0];
    if (!lead.fullName.trim() || !lead.role.trim() || !lead.email.trim()) { toast(t("authPages.orgReg.alertMember")); return; }
    sessionStorage.setItem("orgOnboardingStepThree", JSON.stringify({ formData, documents }));
    navigate("/reeeg");
  };

  // Shared styles
  const inputStyle = { width: "100%", padding: isMobile ? "10px 12px" : "11px 14px", borderRadius: "10px", border: "1.5px solid #ddd", fontSize: isMobile ? "13px" : "14px", outline: "none", boxSizing: "border-box", fontFamily: "inherit", transition: "border-color 0.2s", background: "white" };
  const sectionStyle = { background: "white", borderRadius: "14px", padding: isMobile ? "14px 12px" : "20px", border: "1px solid #e9ecef", display: "flex", flexDirection: "column", gap: isMobile ? "12px" : "16px" };
  const sectionTitleStyle = { fontSize: isMobile ? "13px" : "15px", fontWeight: 700, color: "#1a3d2e", margin: 0 };

  if (!stepOneSnapshot || !stepTwoSnapshot) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
      <p style={{ color: "#666" }}>{t("authPages.orgReg.loadingOrgData")}</p>
    </div>
  );

  return (
    <div style={{ background: "#f5f7f0", minHeight: "100vh" }}>

      {/* Progress header */}
      <div style={{ background: "white", borderBottom: "1px solid #e9ecef", padding: isMobile ? "12px 14px" : "16px 24px", position: "sticky", top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: 760, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "6px" }}>
          {[
            { n: 1, label: isMobile ? t("authPages.orgReg.stepProfileShort") : t("authPages.orgReg.stepProfileLong"),   done: true },
            { n: 2, label: isMobile ? t("authPages.orgReg.stepContactShort") : t("authPages.orgReg.stepContactMedium"),        done: true },
            { n: 3, label: isMobile ? t("authPages.orgReg.stepTeamShort") : t("authPages.orgReg.stepTeamLong"),   active: true },
            { n: 4, label: t("authPages.orgReg.stepVerification") },
          ].map((step, i) => (
            <React.Fragment key={step.n}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px", flex: 1 }}>
                <div style={{ width: isMobile ? 28 : 32, height: isMobile ? 28 : 32, borderRadius: "50%", background: step.done ? "#c8e6c9" : step.active ? "#32614d" : "#e9ecef", color: step.done ? "#2e7d32" : step.active ? "white" : "#999", display: "flex", alignItems: "center", justifyContent: "center", fontSize: isMobile ? "12px" : "13px", fontWeight: 700 }}>
                  {step.done ? "✓" : step.n}
                </div>
                <span style={{ fontSize: isMobile ? "9px" : "11px", color: step.active ? "#32614d" : step.done ? "#2e7d32" : "#999", fontWeight: step.active ? 700 : 400, textAlign: "center", whiteSpace: "nowrap" }}>{step.label}</span>
              </div>
              {i < 3 && <div style={{ height: "2px", flex: 2, background: "#e9ecef", marginBottom: isMobile ? "16px" : "20px" }} />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 760, margin: "0 auto", padding: isMobile ? "16px 14px 100px" : "28px 20px 60px" }}>

        {/* Heading + back */}
        <div style={{ display: "flex", alignItems: isMobile ? "flex-start" : "center", justifyContent: "space-between", marginBottom: "16px", flexDirection: isMobile ? "column" : "row", gap: "8px" }}>
          <div>
            <h2 style={{ fontSize: isMobile ? "15px" : "20px", fontWeight: 700, color: "#1a3d2e", margin: "0 0 4px" }}>{t("authPages.orgReg.step3Title")}</h2>
            <p style={{ fontSize: isMobile ? "11px" : "13px", color: "#888", margin: 0 }}>{t("authPages.orgReg.step3Subtitle")}</p>
          </div>
          <Link to="/regnext" style={{ display: "flex", alignItems: "center", gap: "6px", color: "#32614d", fontSize: isMobile ? "12px" : "13px", fontWeight: 600, textDecoration: "none", border: "1.5px solid #32614d", borderRadius: "8px", padding: "6px 12px", whiteSpace: "nowrap" }}>
            <ArrowLeft size={14} /> {t("authPages.orgReg.back")}
          </Link>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: isMobile ? "12px" : "16px" }}>

          {/* Branding */}
          <section style={sectionStyle}>
            <h3 style={sectionTitleStyle}>{t("authPages.orgReg.brandingTitle")}</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: isMobile ? "10px" : "16px" }}>
              {/* Logo */}
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <span style={{ fontSize: isMobile ? "12px" : "13px", fontWeight: 600, color: "#444" }}>{t("authPages.orgReg.logoOptional")}</span>
                <div onClick={() => logoInputRef.current?.click()}
                  style={{ height: isMobile ? "110px" : "140px", borderRadius: "12px", border: "2px dashed #ddd", background: "#fafafa", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "6px", overflow: "hidden", transition: "border-color 0.2s" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "#32614d"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = "#ddd"}>
                  {documents.logo ? (
                    <div style={{ width: "100%", height: "100%", position: "relative" }}>
                      <img src={documents.logo} alt="Logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "rgba(0,0,0,0.45)", color: "white", fontSize: "11px", textAlign: "center", padding: "4px" }}>{t("authPages.orgReg.change")}</div>
                    </div>
                  ) : (
                    <>
                      <Upload size={isMobile ? 20 : 24} color="#aaa" />
                      <span style={{ fontSize: isMobile ? "11px" : "13px", color: "#888", fontWeight: 600 }}>{t("authPages.orgReg.addLogo")}</span>
                      <small style={{ fontSize: "10px", color: "#bbb" }}>{t("authPages.orgReg.upTo8mb")}</small>
                    </>
                  )}
                </div>
                <input type="file" accept="image/*" ref={logoInputRef} style={{ display: "none" }} onChange={e => handleFileUpload(e.target.files?.[0], "logo")} />
              </div>

              {/* Org Presentation */}
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <span style={{ fontSize: isMobile ? "12px" : "13px", fontWeight: 600, color: "#444" }}>{t("authPages.orgReg.orgPresentationOptional")}</span>
                <div onClick={() => posterInputRef.current?.click()}
                  style={{ height: isMobile ? "110px" : "140px", borderRadius: "12px", border: "2px dashed #ddd", background: "#fafafa", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "6px", overflow: "hidden", transition: "border-color 0.2s" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "#32614d"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = "#ddd"}>
                  {documents.orgPresentation ? (
                    documents.orgPresentation.startsWith("data:image/") ? (
                      <div style={{ width: "100%", height: "100%", position: "relative" }}>
                        <img src={documents.orgPresentation} alt="Org presentation" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "rgba(0,0,0,0.45)", color: "white", fontSize: "11px", textAlign: "center", padding: "4px" }}>{t("authPages.orgReg.change")}</div>
                      </div>
                    ) : (
                      <>
                        <FileText size={isMobile ? 20 : 24} color="#32614d" />
                        <span style={{ fontSize: "11px", color: "#32614d", fontWeight: 600, textAlign: "center", padding: "0 6px" }}>{documents.orgPresentationName || t("authPages.orgReg.fileAttached")}</span>
                        <small style={{ fontSize: "10px", color: "#bbb" }}>{t("authPages.orgReg.clickToChange")}</small>
                      </>
                    )
                  ) : (
                    <>
                      <Upload size={isMobile ? 20 : 24} color="#aaa" />
                      <span style={{ fontSize: isMobile ? "11px" : "13px", color: "#888", fontWeight: 600 }}>{t("authPages.orgReg.uploadPresentation")}</span>
                      <small style={{ fontSize: "10px", color: "#bbb" }}>{t("authPages.orgReg.presentationHint")}</small>
                    </>
                  )}
                </div>
                <input type="file" accept=".pdf,.ppt,.pptx,image/*" ref={posterInputRef} style={{ display: "none" }} onChange={e => handleFileUpload(e.target.files?.[0], "orgPresentation")} />
              </div>
            </div>
          </section>

          {/* Team Members */}
          <section style={sectionStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Users size={isMobile ? 16 : 18} color="#32614d" />
              <h3 style={sectionTitleStyle}>{t("authPages.orgReg.teamTitle")} <span style={{ color: "#e53935" }}>*</span></h3>
            </div>
            <p style={{ margin: 0, fontSize: isMobile ? "11px" : "12px", color: "#888" }}>
              {t("authPages.orgReg.teamDesc")}
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: isMobile ? "10px" : "12px" }}>
              {formData.authorizedMembers.map((member, index) => (
                <div key={index} style={{ background: "#f8f9fa", borderRadius: "12px", padding: isMobile ? "12px 10px" : "16px", border: "1px solid #e9ecef", display: "flex", flexDirection: "column", gap: "8px" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontSize: isMobile ? "12px" : "13px", fontWeight: 700, color: "#32614d" }}>{t("authPages.orgReg.member", { n: index + 1 })}</span>
                    {formData.authorizedMembers.length > 1 && (
                      <button type="button" onClick={() => removeMember(index)}
                        style={{ background: "#ffebee", border: "none", borderRadius: "8px", padding: "5px 8px", cursor: "pointer", color: "#e53935", display: "flex", alignItems: "center" }}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  {/* 3 inputs — stacked on mobile */}
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "8px" }}>
                    <input type="text" placeholder={t("authPages.orgReg.fullNamePlaceholder")} value={member.fullName} onChange={e => updateMember(index, "fullName", e.target.value)}
                      required={index === 0} style={inputStyle} onFocus={e => e.target.style.borderColor = "#32614d"} onBlur={e => e.target.style.borderColor = "#ddd"} />
                    <input type="text" placeholder={t("authPages.orgReg.rolePlaceholder")} value={member.role} onChange={e => updateMember(index, "role", e.target.value)}
                      required={index === 0} style={inputStyle} onFocus={e => e.target.style.borderColor = "#32614d"} onBlur={e => e.target.style.borderColor = "#ddd"} />
                  </div>
                  <input type="email" placeholder={t("authPages.orgReg.teamEmailPlaceholder")} value={member.email} onChange={e => updateMember(index, "email", e.target.value)}
                    required={index === 0} style={inputStyle} onFocus={e => e.target.style.borderColor = "#32614d"} onBlur={e => e.target.style.borderColor = "#ddd"} />
                </div>
              ))}
            </div>

            {formData.authorizedMembers.length < 10 && (
              <button type="button" onClick={addMember}
                style={{ background: "none", border: "1.5px dashed #32614d", borderRadius: "8px", color: "#32614d", padding: isMobile ? "9px 14px" : "10px 18px", fontSize: isMobile ? "12px" : "13px", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", fontFamily: "inherit", width: "100%", justifyContent: "center" }}>
                <Plus size={15} /> {t("authPages.orgReg.addTeamMember")}
              </button>
            )}
          </section>

          {/* Submit */}
          <button type="submit"
            style={{ padding: isMobile ? "13px 20px" : "14px 28px", background: "linear-gradient(135deg,#32614d,#2a5240)", color: "white", border: "none", borderRadius: "12px", fontSize: isMobile ? "14px" : "15px", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", fontFamily: "inherit" }}>
            {t("authPages.orgReg.continueVerification")} <ArrowRight size={18} />
          </button>
        </form>
      </div>
    </div>
  );
}

export default Reeg;